import React from "react"
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  FormControlLabel,
  Button,
  Paper,
  Grid,
  FormGroup,
  FormHelperText,
  SelectChangeEvent,
  Switch,
} from "@mui/material"
import {
  CalendarMonth,
  Work,
  AccessTime,
  LocationCity,
  WorkHistory,
  Info,
  ExpandMore,
  ArrowForward,
  Search,
  Language,
} from "@mui/icons-material"
import dayjs from "dayjs"
import { exitReact, formatFullLocation } from "utils"
import { useHistory } from 'react-router'

import {
	Days,
	Positions,
	Basis,
	Requirements,
	isValidPositionId,
	isValidBasisId,
	isValidRequirementId,
	findPositionById,
	findBasisById,
	findRequirementById
 } from "../config"

import { SelectOption } from "../types"
import { Location } from 'components/BillingLocationSelector'
import { 
  DaysSection, 
  IconWrapper, 
  SearchEngineBox, 
  StepDotInner, 
  StepIndicator, 
  StyledCalendarPicker, 
  HeaderPaper, 
  HeaderTitle,
  MainContainer,
  ContentContainer
} from "./FirstStep.styles"
import { TopBarRedesigned } from "components/TopBarRedesigned"

interface FirstStepProps {
    availabilityArray: SelectOption[]
    availabilityArrayError?: string
    setAvailabilityArray: (id: number, checked: boolean) => void
    positionValue?: number;
    positionValueError?: string;
    onPositionValueChange: (value: (typeof Positions)[number]['id']) => void
    basisValue?: number;
    basisValueError?: string;
    onBasisValueChange: (value: (typeof Basis)[number]['id']) => void
    locationValue?: number;
    locationValueError?: string;
    onLocationValueChange: (value: number) => void
    requirementValue?: number;
    requirementValueError?: string;
    onRequirementValueChange: (value: (typeof Requirements)[number]['id']) => void
    locations: Location[]
    startDate: Date | null
    startDateError?: string
    onStartDateChange: (date: Date) => void
    approximateStartDate: boolean
    onSetApproximateStartDate: (checked: boolean) => void
    isRestorativeFlag: boolean
    onSetIsRestorativeFlag: (checked: boolean) => void
    isOrthodonticFlag: boolean
    onSetIsOrthodonticFlag: (checked: boolean) => void
    editedEmail: string
    editedEmailError: string
    notificationEmail: string
    onEditedEmailChange: (value: string) => void
    onClose: () => void
    onContinue: () => void
    isAllowSearchEngines: boolean;
    onAllowSearchEnginesChange: (checked: boolean) => void;
}

const FirstStep: React.FC<FirstStepProps> = ({
    availabilityArray,
    setAvailabilityArray,
    positionValue,
    requirementValue,
    locationValue,
    basisValue,
    availabilityArrayError,
    positionValueError,
    requirementValueError,
    locationValueError,
    basisValueError,
    onBasisValueChange,
    onLocationValueChange,
    onPositionValueChange,
    onRequirementValueChange,
    locations,
    startDate,
    startDateError,
    onStartDateChange,
    approximateStartDate,
    onSetApproximateStartDate,
    isRestorativeFlag,
    onSetIsRestorativeFlag,
    isOrthodonticFlag,
    onSetIsOrthodonticFlag,
    editedEmail,
    editedEmailError,
    onEditedEmailChange,
    notificationEmail,
    onClose,
    onContinue,
    isAllowSearchEngines,
    onAllowSearchEnginesChange
}) => {
  const history = useHistory();
  
  // Event handlers to prevent Framework7 touch interference with Material-UI Select components
  const handleSelectInteraction = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    // Prevent Framework7 touch handling from interfering with Material-UI Select dropdowns
  };

  return (<>
    <TopBarRedesigned
      onBack={() => {
      history.push('loading')
      exitReact('home')
    }}/>
    <MainContainer>
      <ContentContainer>
        <HeaderPaper>
            <HeaderTitle>
              Post a Permanent / Contract Position
            </HeaderTitle>
        </HeaderPaper>

        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Box sx={{ 
            width: 10, 
            height: 10, 
            bgcolor: "#4CAF50", 
            borderRadius: "50%",
            display: "inline-block"
          }} />
          <Typography variant="body2" color="text.primary" sx={{ ml: 1, fontWeight: "medium" }}>
            Step 1 of 2
          </Typography>
        </Box>

        <Box sx={{ 
          width: "100%", 
          height: "4px", 
          bgcolor: "#e0e0e0", 
          mb: 4, 
          borderRadius: 2,
          position: "relative",
          overflow: "hidden" 
        }}>
          <Box sx={{ 
            position: "absolute", 
            width: "50%", 
            height: "100%", 
            bgcolor: "var(--primary-blue)",
            borderRadius: 2
          }} />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Role */}
          <Box>
            <IconWrapper color="var(--primary-blue)">
              <Work sx={{
                fontSize: "1.5rem",
                padding: "4px",
                bgcolor: "#e3f2fd",
                borderRadius: '8px',
              }}/>
              <Typography fontWeight="medium" fontSize="1rem">Role</Typography>
            </IconWrapper>
            <FormControl fullWidth error={!!positionValueError}>
              <Select
                id="position"
                name="position"
                value={positionValue !== undefined ? positionValue : ''}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  const numValue = Number(e.target.value);
                  if (isValidPositionId(numValue)) {
                    onPositionValueChange(numValue);
                  }
                }}
                onTouchStart={handleSelectInteraction}
                onMouseDown={handleSelectInteraction}
                displayEmpty
                renderValue={(selected) => {
                  if (selected === '') {
                    return "Select a role"
                  }
                  const position = findPositionById(Number(selected));
                  return position ? position.text : String(selected);
                }}
                sx={{ 
                  borderRadius: 1,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0"
                  }
                }}
              >
                {Positions.map(({id, text}) => (
                  <MenuItem key={id} value={id}>{text}</MenuItem>
                ))}
              </Select>
              {positionValueError && <FormHelperText>{positionValueError}</FormHelperText>}
            </FormControl>
          </Box>

          {/* Employment Type */}
          <Box>
            <IconWrapper color="var(--primary-blue)" >
              <AccessTime sx={{
                fontSize: "1.5rem",
                padding: "4px",
                bgcolor: "#e3f2fd",
                borderRadius: '8px',
              }}/>
              <Typography fontWeight="medium" fontSize="1rem">Employment Type</Typography>
            </IconWrapper>
            <FormControl fullWidth error={!!basisValueError}>
              <Select
                id="basis"
                name="basis"
                value={basisValue !== undefined ? basisValue : ''}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  const numValue = Number(e.target.value);
                  if (isValidBasisId(numValue)) {
                    onBasisValueChange(numValue);
                  }
                }}
                onTouchStart={handleSelectInteraction}
                onMouseDown={handleSelectInteraction}
                displayEmpty
                renderValue={(selected) => {
                  if (selected === '') {
                    return "Select employment type"
                  }
                  const basis = findBasisById(Number(selected));
                  return basis ? basis.text : String(selected);
                }}
                sx={{ 
                  borderRadius: 1,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0"
                  }
                }}
              >
                {Basis.map(({id, text}) => (
                  <MenuItem key={id} value={id}>{text}</MenuItem>
                ))}
              </Select>
              {basisValueError && <FormHelperText>{basisValueError}</FormHelperText>}
            </FormControl>
          </Box>

          {/* Office Location */}
          <Box>
            <IconWrapper color="var(--primary-blue)">
              <LocationCity sx={{
                fontSize: "1.5rem",
                padding: "4px",
                bgcolor: "#e3f2fd", 
                borderRadius: '8px',
              }}/>
              <Typography fontWeight="medium" fontSize="1rem">Office Location</Typography>
            </IconWrapper>
            <FormControl fullWidth error={!!locationValueError}>
              <Select
                id="locations"
                name="locations"
                value={locationValue !== undefined ? locationValue : ''}
                onChange={(e: SelectChangeEvent<unknown>) => onLocationValueChange(Number(e.target.value))}
                onTouchStart={handleSelectInteraction}
                onMouseDown={handleSelectInteraction}
                displayEmpty
                renderValue={(selected) => {
                  if (selected === '') {
                    return "Office Location"
                  }
                  const location = locations.find(loc => loc.id === Number(selected));
                  return location ? formatFullLocation(location) : String(selected);
                }}
                sx={{ 
                  borderRadius: 1,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0"
                  }
                }}
              >
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>{formatFullLocation(location)}</MenuItem>
                ))}
              </Select>
              {locationValueError && <FormHelperText>{locationValueError}</FormHelperText>}
            </FormControl>
          </Box>

          {/* Days */}
          <Box sx={{ 
            bgcolor: "#f5f8ff", 
            p: 3, 
            borderRadius: 2,
            border: "1px solid #e0e0e0"
          }}>
            <Typography fontWeight="medium" sx={{ mb: 2 }}>
              What days do you need to fill with this position?
            </Typography>
            <FormControl 
              component="fieldset" 
              error={!!availabilityArrayError}
              sx={{ width: "100%" }}
            >
              <FormGroup>
                <Grid container spacing={2}>
                  {Days.map(({id, text}) => (
                    <Grid item xs={6} sm={4} key={id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={availabilityArray.some(option => option.id === id)}
                            onChange={(_, checked) => setAvailabilityArray(id, checked)}
                            sx={{
                              color: "var(--primary-blue)",
                              '&.Mui-checked': {
                                color: "var(--primary-blue)",
                              },
                            }}
                          />
                        }
                        label={text}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
              {availabilityArrayError && <FormHelperText>{availabilityArrayError}</FormHelperText>}
            </FormControl>
          </Box>

          {/* Minimum experience */}
          <Box>
            <IconWrapper color="var(--primary-blue)">
              <WorkHistory sx={{
                fontSize: "1.5rem",
                padding: "4px",
                bgcolor: "#e3f2fd",
                borderRadius: '8px',
              }}/>
              <Typography fontWeight="medium" fontSize="1rem">Minimum Experience</Typography>
            </IconWrapper>
            <FormControl fullWidth error={!!requirementValueError}>
              <Select
                id="requirement"
                name="requirement"
                value={requirementValue !== undefined ? requirementValue : ''}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  const numValue = Number(e.target.value);
                  if (isValidRequirementId(numValue)) {
                    onRequirementValueChange(numValue);
                  }
                }}
                onTouchStart={handleSelectInteraction}
                onMouseDown={handleSelectInteraction}
                displayEmpty
                renderValue={(selected) => {
                  if (selected === '') {
                    return "Minimum Experience"
                  }
                  const requirement = findRequirementById(Number(selected));
                  return requirement ? requirement.text : String(selected);
                }}
                sx={{ 
                  borderRadius: 1,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0"
                  }
                }}
              >
                {Requirements.map(({id, text}) => (
                  <MenuItem key={id} value={id}>{text}</MenuItem>
                ))}
              </Select>
              {requirementValueError && <FormHelperText>{requirementValueError}</FormHelperText>}
            </FormControl>
          </Box>

          {/* Start Date */}
          <Box>
            <IconWrapper color="var(--primary-blue)">
              <CalendarMonth sx={{
                fontSize: "1.5rem",
                padding: "4px",
                bgcolor: "#e3f2fd",
                borderRadius: '8px',
              }}/>
              <Typography fontWeight="medium" fontSize="1rem">Start Date</Typography>
            </IconWrapper>
            <StyledCalendarPicker
              label={''}
              value={dayjs(startDate).isValid() ? dayjs(startDate) : undefined}
              onChange={(date) => date && onStartDateChange(date.toDate())}
              name={'startDate'}
              error={startDateError}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={approximateStartDate}
                  onChange={(_, checked) => onSetApproximateStartDate(checked)}
                  sx={{
                    color: "var(--primary-blue)",
                    '&.Mui-checked': {
                      color: "var(--primary-blue)",
                    },
                  }}
                />
              }
              label="Start Date is Approximate"
              sx={{ mt: 1 }}
            />
          </Box>

          {/* Allow search engines */}
          <Paper sx={{ 
            p: 3, 
            borderRadius: 2, 
            bgcolor: "#f5f8ff", 
            border: "1px solid #e0e0e0"
          }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography fontWeight="medium">Allow search engines to display your ad</Typography>
                <Typography variant="body2" color="var(--primary-blue)">
                  Recommended - more applicants will see it
                </Typography>
              </Box>
              <Switch
                checked={isAllowSearchEngines}
                onChange={(_, checked) => onAllowSearchEnginesChange(checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: "var(--primary-blue)",
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: "var(--primary-blue)",
                  },
                }}
              />
            </Box>
          </Paper>

          {positionValue === Positions[0].id && (
            <Box>
              <Typography 
                fontWeight="medium" 
                fontSize="1rem" 
                sx={{ 
                  color: "var(--primary-blue)",
                  mb: 1
                }}
              >
                Additional Skills (optional)
              </Typography>
              <Paper 
                sx={{ 
                  p: 0, 
                  borderRadius: 2, 
                  bgcolor: "#f5f8ff", 
                  border: "1px solid #e0e0e0",
                  overflow: 'hidden'
                }}
              >
                <Box
                  onClick={() => {
                    const element = document.getElementById('skills-content');
                    if (element) {
                      element.style.display = element.style.display === 'none' ? 'block' : 'none';
                    }
                  }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#edf1fa',
                    },
                  }}
                >
                  <Typography>
                    {(isOrthodonticFlag || isRestorativeFlag) 
                      ? [
                          ...(isOrthodonticFlag ? ['Orthodontic Exp.'] : []),
                          ...(isRestorativeFlag ? ['Restorative Exp.'] : [])
                        ].join(', ')
                      : 'Select additional skills'
                    }
                  </Typography>
                  <ExpandMore />
                </Box>
                
                {/* Dropdown content */}
                <Box 
                  id="skills-content" 
                  sx={{ 
                    display: 'none',
                    p: 2,
                    borderTop: '1px solid #e0e0e0',
                    bgcolor: "#edf1fa"
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Checkbox
                      checked={isOrthodonticFlag}
                      onChange={(_, checked) => onSetIsOrthodonticFlag(checked)}
                      sx={{
                        color: "var(--primary-blue)",
                        '&.Mui-checked': {
                          color: "var(--primary-blue)",
                        },
                      }}
                    />
                    <Typography>Orthodontic Exp.</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={isRestorativeFlag}
                      onChange={(_, checked) => onSetIsRestorativeFlag(checked)}
                      sx={{
                        color: "var(--primary-blue)",
                        '&.Mui-checked': {
                          color: "var(--primary-blue)",
                        },
                      }}
                    />
                    <Typography>Restorative Exp.</Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}

          <Box>
            <Typography fontSize="0.9rem">
              By default, we send applicant notifications to your account email,
              but if you want them sent to a different email address, enter it below:
            </Typography>
            <TextField
              fullWidth
              margin="normal"
              placeholder={notificationEmail}
              value={editedEmail}
              onChange={(e) => onEditedEmailChange(e.target.value)}
              error={!!editedEmailError}
              helperText={editedEmailError}
              sx={{ 
                mt: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1
                }
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              onClick={onContinue}
              endIcon={<ArrowForward />}
              sx={{
                bgcolor: "var(--primary-blue)",
                borderRadius: 10,
                py: 1.5,
                textTransform: "uppercase",
                fontWeight: "bold",
                boxShadow: 2,
                '&:hover': {
                  bgcolor: "#002266",
                },
              }}
            >
              Continue
            </Button>
            <Button
              variant="text"
              onClick={onClose}
              sx={{
                color: "var(--primary-blue)",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </ContentContainer>
    </MainContainer>
  </>)
}

export default FirstStep;
